import { useEffect, useState, useCallback } from "react";
import { isActiveEmp } from "../lib/constants";
import { toast } from "sonner";
import {
  HeartPulse, Loader2, FolderOpen, Plus, ArrowRight, Trash2, Upload, FileText,
  MapPin, ExternalLink, Search, ShieldCheck, Image as ImageIcon, CalendarDays, Download,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { api } from "../lib/api";
import { FileLightbox, isPdfFile } from "./FileLightbox";
import { InjuryFolderImport } from "./InjuryFolderImport";
import { HealthBadge, HealthControl, AddDocButton } from "./InjuryExtras";
import { Pencil } from "lucide-react";
import { personMatch } from "../lib/searchText";

const injDays = (i) => {
  const d0 = Date.parse((i.injury_date || String(i.opened_at || "").slice(0, 10)) + "T00:00:00");
  if (Number.isNaN(d0)) return null;
  const end = i.status === "closed" ? Date.parse(String(i.closed_at || i.updated_at || "").slice(0, 10) + "T00:00:00") || Date.now() : Date.now();
  return Math.max(0, Math.floor((end - d0) / 86400000));
};
const DaysBadge = ({ i }) => {
  const n = injDays(i);
  if (n === null) return null;
  const closed = i.status === "closed";
  const cls = closed ? "bg-slate-100 text-slate-500 border-slate-200" : n > 30 ? "bg-red-50 text-red-700 border-red-200" : n > 14 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`} data-testid={`inj-days-${i.id}`}>{closed ? `استمرت ${n} يوم` : `مفتوحة منذ ${n} يوم`}</span>;
};
const STATUS_LABEL = { open: "مفتوح", submitted: "مُرسل من المندوب", closed: "مغلق" };
const STATUS_CLS = {
  open: "bg-amber-50 text-amber-700 border-amber-200",
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};
const isImg = (ct) => (ct || "").startsWith("image/");

const AttachmentThumb = ({ inj, file, canEdit, onDelete, onOpen }) => (
  <div className="relative group rounded-lg border border-slate-200 overflow-hidden bg-slate-50" data-testid={`inj-att-${file.file_id}`}>
    <button type="button" onClick={() => onOpen(api.injuryFileUrl(inj.id, file.file_id), file.name, isPdfFile(file.name, file.content_type))} className="block w-full">
      {isImg(file.content_type) ? (
        <img src={api.injuryFileUrl(inj.id, file.file_id)} alt={file.name} className="h-24 w-full object-cover" />
      ) : (
        <div className="h-24 w-full flex flex-col items-center justify-center text-slate-400 gap-1">
          <FileText className="h-6 w-6" /><span className="text-[10px] px-1 truncate max-w-full">{file.name}</span>
        </div>
      )}
    </button>
    {canEdit && (
      <button onClick={() => onDelete(file.file_id)} data-testid={`inj-att-del-${file.file_id}`}
        className="absolute top-1 left-1 rounded-full bg-white/95 text-rose-600 p-1 shadow border border-rose-100 hover:bg-rose-50">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

const CategoryBox = ({ inj, cat, label, files, canEdit, onUpload, onDelete, onOpen, custom, onRename, onRemove }) => {
  const [busy, setBusy] = useState(false);
  const inputId = `inj-up-${cat}`;
  const handle = async (e) => {
    const list = Array.from(e.target.files || []); e.target.value = "";
    if (!list.length) return;
    setBusy(true);
    try { for (const f of list) await onUpload(cat, f); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid={`inj-cat-${cat}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><ImageIcon className="h-4 w-4 text-slate-400" />{label}
          {files.length > 0 && <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-1.5">{files.length}</span>}
          {custom && canEdit && <>
            <button type="button" onClick={() => onRename(cat, label)} title="إعادة تسمية" data-testid={`inj-cat-rename-${cat}`} className="text-slate-300 hover:text-blue-600"><Pencil className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => onRemove(cat, label)} title="حذف المستند" data-testid={`inj-cat-remove-${cat}`} className="text-slate-300 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
          </>}
        </span>
        {canEdit && (
          <>
            <input id={inputId} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={handle} data-testid={`inj-upload-${cat}`} />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => document.getElementById(inputId).click()} className="h-7 gap-1 text-xs">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} رفع
            </Button>
          </>
        )}
      </div>
      {files.length === 0 ? (
        <p className="text-xs text-slate-300 py-2 text-center">لا توجد ملفات</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {files.map((f) => <AttachmentThumb key={f.file_id} inj={inj} file={f} canEdit={canEdit} onDelete={onDelete} onOpen={onOpen} />)}
        </div>
      )}
    </div>
  );
};

const InjuryDetail = ({ id, canEdit, onClose, onChanged }) => {
  const [inj, setInj] = useState(null);
  const [lb, setLb] = useState(null);
  const openLb = (url, name, isPdf) => setLb({ url, name, isPdf });
  const load = useCallback(async () => {
    try { setInj(await api.getInjury(id)); } catch { toast.error("تعذّر تحميل البلاغ"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (cat, file) => {
    try { await api.uploadInjuryFile(id, cat, file); await load(); onChanged?.(); toast.success("تم رفع الملف"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الرفع"); }
  };
  const delFile = async (fid) => {
    if (!window.confirm("حذف هذا الملف؟")) return;
    try { await api.deleteInjuryFile(id, fid); await load(); onChanged?.(); } catch { toast.error("تعذّر الحذف"); }
  };
  const saveMeta = async (patch) => {
    try { await api.updateInjury(id, patch); await load(); onChanged?.(); toast.success("تم الحفظ"); }
    catch { toast.error("تعذّر الحفظ"); }
  };

  const renameCat = async (key, old) => {
    const label = window.prompt("اسم المستند", old);
    if (!label || label.trim() === old) return;
    try { await api.renameInjuryCategory(id, key, label.trim()); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت إعادة التسمية"); }
  };
  const removeCat = async (key, label) => {
    if (!window.confirm(`حذف المستند «${label}» وكل ملفاته؟`)) return;
    try { await api.deleteInjuryCategory(id, key); await load(); onChanged?.(); } catch { toast.error("تعذّر الحذف"); }
  };

  if (!inj) return <div className="py-16 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const loc = inj.location || {};
  const mapsLink = loc.gmaps_link || loc.manual_link || (loc.lat ? `https://maps.google.com/?q=${loc.lat},${loc.lng}` : "");
  const cats = inj.categories || {};
  const byCat = inj.attachments_by_category || {};

  return (
    <div className="space-y-4" data-testid="injury-detail">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <span className="font-heading font-bold text-slate-900">{inj.employee_name}</span>
          <span className="text-xs text-slate-400 font-mono-nums" dir="ltr">{inj.iqama_number}</span>
          <span className={`text-[11px] rounded-full border px-2 py-0.5 ${STATUS_CLS[inj.status] || ""}`}>{STATUS_LABEL[inj.status] || inj.status}</span>
          <DaysBadge i={inj} />
          <HealthControl inj={inj} canEdit={canEdit} onSaved={() => { load(); onChanged?.(); }} />
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="rounded-lg bg-slate-100 px-2 py-1 font-mono-nums">رقم الإصابة: {inj.injury_number}</span>
          <span className="rounded-lg bg-slate-100 px-2 py-1 flex items-center gap-1"><CalendarDays className="h-3 w-3" />{inj.injury_date} · {inj.opened_day}</span>
        </div>
      </div>

      {canEdit && (
        <div className="grid gap-2 sm:grid-cols-3">
          <Input data-testid="inj-edit-number" defaultValue={inj.injury_number} placeholder="رقم الإصابة"
            onBlur={(e) => e.target.value !== inj.injury_number && saveMeta({ injury_number: e.target.value })} className="h-9" />
          <Input data-testid="inj-edit-date" type="date" defaultValue={inj.injury_date}
            onBlur={(e) => e.target.value !== inj.injury_date && saveMeta({ injury_date: e.target.value })} className="h-9" />
          <Select value={inj.status} onValueChange={(v) => saveMeta({ status: v })}>
            <SelectTrigger className="h-9" data-testid="inj-edit-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">مفتوح</SelectItem>
              <SelectItem value="submitted">مُرسل</SelectItem>
              <SelectItem value="closed">مغلق</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {mapsLink && (
        <a href={mapsLink} target="_blank" rel="noreferrer" data-testid="inj-location-link"
          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 py-1.5 text-sm hover:bg-emerald-100">
          <MapPin className="h-4 w-4" /> موقع الحادث على الخريطة <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {inj.attachment_count > 0 && (
        <a href={api.injuryExportUrl(inj.id)} target="_blank" rel="noreferrer" data-testid="inj-export-btn"
          className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 px-3 py-1.5 text-sm hover:bg-indigo-100 mr-2">
          <Download className="h-4 w-4" /> تصدير مجمّع (ZIP) · {inj.attachment_count}
        </a>
      )}
      {inj.notes && <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-2">{inj.notes}</p>}

      {inj.auto_docs?.length > 0 && (
        <div className="rounded-xl border border-teal-200 bg-teal-50/50 p-3">
          <p className="text-sm font-bold text-teal-800 mb-2 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> وثائق المندوب (تلقائية)</p>
          <div className="flex flex-wrap gap-2">
            {inj.auto_docs.map((d) => (
              <button key={d.file_id} type="button" onClick={() => openLb(api.injuryFileUrl(inj.id, d.file_id), d.label, isPdfFile(d.name))} data-testid={`inj-autodoc-${d.key}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-teal-300 bg-white px-3 py-1 text-xs text-teal-700 hover:bg-teal-100">
                <FileText className="h-3.5 w-3.5" /> {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {Object.entries(cats).map(([cat, label]) => (
          <CategoryBox key={cat} inj={inj} cat={cat} label={label} files={byCat[cat] || []}
            canEdit={canEdit} onUpload={upload} onDelete={delFile} onOpen={openLb}
            custom={(inj.custom_categories || []).includes(cat)} onRename={renameCat} onRemove={removeCat} />
        ))}
        {canEdit && <AddDocButton inj={inj} onAdded={load} />}
      </div>
      <FileLightbox open={!!lb} onOpenChange={(o) => !o && setLb(null)} url={lb?.url} name={lb?.name} isPdf={lb?.isPdf} />
    </div>
  );
};

export const InjuriesPanel = ({ canEdit, employees = [] }) => {
  const [data, setData] = useState(null);
  const [folder, setFolder] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addEmp, setAddEmp] = useState("");
  const [addNum, setAddNum] = useState("");
  const [addDate, setAddDate] = useState("");

  const load = useCallback(async () => {
    try { setData(await api.listInjuries()); } catch { toast.error("تعذّر التحميل"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!addEmp) { toast.error("اختر المندوب"); return; }
    try {
      const r = await api.createInjury({ employee_id: addEmp, injury_number: addNum, injury_date: addDate });
      toast.success("تم إنشاء ملف إصابة");
      setAddOpen(false); setAddEmp(""); setAddNum(""); setAddDate("");
      await load(); setDetailId(r.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإنشاء"); }
  };
  const delInjury = async (id) => {
    if (!window.confirm("حذف ملف الإصابة وكل ملفاته؟")) return;
    try { await api.deleteInjury(id); toast.success("تم الحذف"); setDetailId(null); await load(); }
    catch { toast.error("تعذّر الحذف"); }
  };

  const folders = (data?.folders || []).filter((f) => !q.trim() ||
    personMatch(f, q));
  const folderItems = folder ? (data?.items || []).filter((i) => i.employee_id === folder) : [];

  return (
    <div className="space-y-4" data-testid="injuries-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2"><HeartPulse className="h-5 w-5 text-rose-500" /> إصابات العمل</h3>
        <div className="flex flex-wrap items-center gap-2">
          {!folder && (
            <div className="relative">
              <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input data-testid="inj-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث باسم المندوب أو الهوية" className="h-9 w-56 pr-8" />
            </div>
          )}
          {canEdit && <InjuryFolderImport employees={employees} onDone={load} />}
          {canEdit && <Button data-testid="inj-add-btn" onClick={() => setAddOpen(true)} className="h-9 gap-1.5 bg-rose-600 hover:bg-rose-700"><Plus className="h-4 w-4" /> ملف إصابة</Button>}
        </div>
      </div>

      {!data ? (
        <div className="py-16 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : folder ? (
        <>
          <button data-testid="inj-back" onClick={() => setFolder(null)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
            <ArrowRight className="h-4 w-4" /> رجوع للمجلدات
          </button>
          <div className="grid gap-2 sm:grid-cols-2">
            {folderItems.map((i) => (
              <button key={i.id} data-testid={`inj-item-${i.id}`} onClick={() => setDetailId(i.id)}
                className="text-right rounded-2xl border border-slate-200 bg-white p-3 hover:border-rose-300 hover:shadow-sm transition">
                <div className="flex items-center justify-between">
                  <span className="font-mono-nums font-bold text-slate-800">{i.injury_number}</span>
                  <span className={`text-[11px] rounded-full border px-2 py-0.5 ${STATUS_CLS[i.status] || ""}`}>{STATUS_LABEL[i.status] || i.status}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500 flex items-center gap-2">
                  <CalendarDays className="h-3.5 w-3.5" />{i.injury_date} · {i.opened_day}
                  <span className="mr-auto flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" />{i.attachment_count}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5"><DaysBadge i={i} /><HealthBadge h={i.health} testid={`inj-health-row-${i.id}`} /></div>
              </button>
            ))}
          </div>
        </>
      ) : folders.length === 0 ? (
        <div className="py-16 text-center text-slate-400">لا توجد إصابات مسجّلة بعد.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {folders.map((f) => (
            <button key={f.employee_id} data-testid={`inj-folder-${f.employee_id}`} onClick={() => setFolder(f.employee_id)}
              className="text-right rounded-2xl border border-slate-200 bg-white p-4 hover:border-rose-300 hover:shadow-sm transition">
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-50 text-rose-500"><FolderOpen className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-800 truncate">{f.employee_name || "—"}</p>
                  <p className="text-[11px] text-slate-400 font-mono-nums" dir="ltr">{f.iqama_number || ""}</p>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">{f.count} إصابة</span>
                {f.open > 0 && <span className="rounded-full bg-amber-50 text-amber-700 px-2 py-0.5">{f.open} نشطة</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2 font-heading pl-6">
              <span className="flex items-center gap-2"><HeartPulse className="h-5 w-5 text-rose-500" /> ملف إصابة عمل</span>
              {canEdit && detailId && (
                <Button size="sm" variant="outline" onClick={() => delInjury(detailId)} data-testid="inj-delete-btn" className="h-7 gap-1 border-rose-200 text-rose-600 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" /> حذف
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {detailId && <InjuryDetail id={detailId} canEdit={canEdit} onChanged={load} onClose={() => setDetailId(null)} />}
        </DialogContent>
      </Dialog>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader><DialogTitle className="font-heading">ملف إصابة جديد</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">المندوب</label>
              <Select value={addEmp} onValueChange={setAddEmp}>
                <SelectTrigger data-testid="inj-add-emp" className="h-10"><SelectValue placeholder="اختر المندوب" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {employees.filter(isActiveEmp).map((e) => <SelectItem key={e.id} value={e.id}>{e.name} {e.iqama_number ? `· ${e.iqama_number}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-slate-500">رقم الإصابة (اختياري)</label>
                <Input data-testid="inj-add-num" value={addNum} onChange={(e) => setAddNum(e.target.value)} placeholder="تلقائي" className="h-10" /></div>
              <div><label className="text-xs text-slate-500">تاريخ الإصابة</label>
                <Input data-testid="inj-add-date" type="date" value={addDate} onChange={(e) => setAddDate(e.target.value)} className="h-10" /></div>
            </div>
            <Button data-testid="inj-add-confirm" onClick={create} className="w-full bg-rose-600 hover:bg-rose-700">إنشاء الملف</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
